import React, { useState, useEffect } from "react";
import {
 Row,
 Col,
 Card,
 Button,
 Form,
 Table,
 Badge,
 Spinner,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import {
 createSafetyIncident,
 updateSafetyIncident,
 getSafetyIncident,
} from "../../../services/safetyService";

/* Constants */
const INCIDENT_TYPES = [
 "Near Miss", "First Aid", "Medical Treatment", "Lost Time Injury", "Fatality",
 "Property Damage", "Environmental", "Fire", "Electrical Hazard", "Chemical Spill",
 "Fall from Height", "Struck By Object", "Caught In/Between",
 "Safety Observation", "Unsafe Act", "Unsafe Condition",
 "Toolbox Talk", "Safety Audit", "Compliance Check", "Other",
];

const CHECKLIST_TEMPLATES = {
 "Safety Audit": [
 "PPE worn correctly by all workers",
 "Work area is properly barricaded",
 "Fire extinguisher present and accessible",
 "First aid kit available and stocked",
 "Toolbox talk conducted before work start",
 "Emergency evacuation plan posted",
 "Housekeeping standards maintained",
 "Permit to work obtained if required",
 "Electrical safety checks completed",
 "Scaffolding inspection done",
 ],
 "Compliance Check": [
 "Safety signage displayed",
 "Personal Protective Equipment available",
 "Safety induction conducted for new workers",
 "Incident reporting procedure in place",
 "Medical examination records up to date",
 "Hazardous material storage compliant",
 "Waste disposal procedures followed",
 ],
};

const initialCorrectiveAction = {
 action: "",
 assignedTo: "",
 dueDate: "",
 status: "Open",
 remarks: "",
};

const initialChecklistItem = { item: "", compliant: "Pending", remarks: "" };
const initialWitness = { name: "", designation: "", statement: "" };
const initialAttendee = { name: "", designation: "" };

const initialForm = {
 incidentDate: new Date().toISOString().slice(0, 10),
 incidentTime: "",
 incidentType: "Near Miss",
 severity: "Low",
 location: "",
 description: "",
 involvedPerson: "",
 designation: "",
 employer: "",
 injuryType: "",
 bodyPartAffected: "",
 medicalTreatmentProvided: false,
 medicalDetails: "",
 immediateCause: "",
 rootCause: "",
 contributingFactors: "",
 preventiveMeasures: "",
 lessonsLearned: "",
 investigationRequired: false,
 toolboxTalkTopic: "",
 attendeesCount: 0,
 likelihoodScore: "",
 consequenceScore: "",
 status: "Draft",
 project: "",
 workOrder: "",
 reportedBy: "",
 investigatedBy: "",
 witnesses: [],
 correctiveActions: [],
 checklistItems: [],
 attendees: [],
};

/* Component */
const SafetyForm = ({ mode }) => {
 const navigate = useNavigate();
 const { id } = useParams();
 const isEdit = mode === "edit";

 const [form, setForm] = useState(initialForm);
 const [loading, setLoading] = useState(false);
 const [fetching, setFetching] = useState(false);
 const [projects, setProjects] = useState([]);
 const [workOrders, setWorkOrders] = useState([]);
 const [users, setUsers] = useState([]);
 const [newPhotos, setNewPhotos] = useState([]);
 const [existingPhotos, setExistingPhotos] = useState([]);

 /* Load reference data */
  useEffect(() => {
    const load = async () => {
      try {
        const [pRes, uRes, wRes] = await Promise.all([
          api.get("/projects?limit=1000"),
          api.get("/users?limit=1000"),
          api.get("/work-orders?limit=1000"),
        ]);
        setProjects(pRes.data?.data || []);
        setUsers(uRes.data?.data || []);
        setWorkOrders(wRes.data?.data || []);
 } catch (_) {}
 };
 load();
 }, []);

 /* Load work orders when project changes */
 useEffect(() => {
 if (!form.project) {
 setWorkOrders([]);
 return;
 }
 api
 .get("/work-orders", { params: { project: form.project, limit: 500 } })
 .then((res) => setWorkOrders(res.data?.data || []))
 .catch(() => {});
 }, [form.project]);

 /* Pre-fill checklist when type changes */
 useEffect(() => {
 if (!isEdit && CHECKLIST_TEMPLATES[form.incidentType]) {
 setForm((prev) => ({
 ...prev,
 checklistItems: CHECKLIST_TEMPLATES[form.incidentType].map((item) => ({
 ...initialChecklistItem,
 item,
 })),
 }));
 } else if (!isEdit) {
 setForm((prev) => ({ ...prev, checklistItems: [] }));
 }
 }, [form.incidentType, isEdit]);

 /* Load record when editing */
 useEffect(() => {
 if (!isEdit || !id) return;
 setFetching(true);
 getSafetyIncident(id)
 .then((res) => {
 const r = res.data?.data;
 if (!r) return;
 setForm({
 incidentDate: r.incidentDate ? r.incidentDate.slice(0, 10) : "",
 incidentTime: r.incidentTime || "",
 incidentType: r.incidentType || "Near Miss",
 severity: r.severity || "Low",
 location: r.location || "",
 description: r.description || "",
 involvedPerson: r.involvedPerson || "",
 designation: r.designation || "",
 employer: r.employer || "",
 injuryType: r.injuryType || "",
 bodyPartAffected: r.bodyPartAffected || "",
 medicalTreatmentProvided: r.medicalTreatmentProvided || false,
 medicalDetails: r.medicalDetails || "",
 immediateCause: r.immediateCause || "",
 rootCause: r.rootCause || "",
 contributingFactors: r.contributingFactors || "",
 preventiveMeasures: r.preventiveMeasures || "",
 lessonsLearned: r.lessonsLearned || "",
 investigationRequired: r.investigationRequired || false,
 toolboxTalkTopic: r.toolboxTalkTopic || "",
 attendeesCount: r.attendeesCount || 0,
 likelihoodScore: r.likelihoodScore || "",
 consequenceScore: r.consequenceScore || "",
 status: r.status || "Draft",
 project: r.project?._id || r.project || "",
 workOrder: r.workOrder?._id || r.workOrder || "",
 reportedBy: r.reportedBy?._id || r.reportedBy || "",
 investigatedBy: r.investigatedBy?._id || r.investigatedBy || "",
 witnesses: (r.witnesses || []).map((w) => ({ ...w })),
 correctiveActions: (r.correctiveActions || []).map((ca) => ({
 ...ca,
 assignedTo: ca.assignedTo?._id || ca.assignedTo || "",
 dueDate: ca.dueDate ? ca.dueDate.slice(0, 10) : "",
 completedDate: ca.completedDate ? ca.completedDate.slice(0, 10) : "",
 })),
 checklistItems: (r.checklistItems || []).map((c) => ({ ...c })),
 attendees: (r.attendees || []).map((a) => ({ ...a })),
 });
 setExistingPhotos(r.photos || []);
 })
 .catch(() => toast.error("Failed to load record"))
 .finally(() => setFetching(false));
 }, [isEdit, id]);

 /* Handlers */
 const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === "checkbox" ? checked : value;

    setForm((prev) => {
      const next = { ...prev, [name]: val };
      if (name === "project") {
        if (val && prev.workOrder) {
          const currentWo = workOrders.find((w) => String(w._id) === String(prev.workOrder));
          const currentWoProjId = currentWo?.project?._id || currentWo?.project;
          if (currentWoProjId && String(currentWoProjId) !== String(val)) {
            next.workOrder = "";
          }
        }
      } else if (name === "workOrder") {
        if (val) {
          const selectedWo = workOrders.find((w) => String(w._id) === String(val));
          const autoProjId = selectedWo?.project?._id || selectedWo?.project;
          if (autoProjId) {
            next.project = String(autoProjId);
          }
        }
      }
      return next;
    });
  };

 /* Corrective Actions */
 const addCorrectiveAction = () =>
 setForm((prev) => ({
 ...prev,
 correctiveActions: [...prev.correctiveActions, { ...initialCorrectiveAction }],
 }));

 const removeCorrectiveAction = (idx) =>
 setForm((prev) => ({
 ...prev,
 correctiveActions: prev.correctiveActions.filter((_, i) => i !== idx),
 }));

 const updateCA = (idx, field, value) => {
 setForm((prev) => {
 const cas = [...prev.correctiveActions];
 cas[idx] = { ...cas[idx], [field]: value };
 return { ...prev, correctiveActions: cas };
 });
 };

 /* Checklist */
 const addChecklistItem = () =>
 setForm((prev) => ({
 ...prev,
 checklistItems: [...prev.checklistItems, { ...initialChecklistItem }],
 }));

 const removeChecklistItem = (idx) =>
 setForm((prev) => ({
 ...prev,
 checklistItems: prev.checklistItems.filter((_, i) => i !== idx),
 }));

 const updateChecklist = (idx, field, value) => {
 setForm((prev) => {
 const items = [...prev.checklistItems];
 items[idx] = { ...items[idx], [field]: value };
 return { ...prev, checklistItems: items };
 });
 };

 /* Witnesses */
 const addWitness = () =>
 setForm((prev) => ({
 ...prev,
 witnesses: [...prev.witnesses, { ...initialWitness }],
 }));

 const removeWitness = (idx) =>
 setForm((prev) => ({
 ...prev,
 witnesses: prev.witnesses.filter((_, i) => i !== idx),
 }));

 const updateWitness = (idx, field, value) => {
 setForm((prev) => {
 const arr = [...prev.witnesses];
 arr[idx] = { ...arr[idx], [field]: value };
 return { ...prev, witnesses: arr };
 });
 };

 /* Attendees (Toolbox Talk) */
 const addAttendee = () =>
 setForm((prev) => ({
 ...prev,
 attendees: [...prev.attendees, { ...initialAttendee }],
 }));

 const removeAttendee = (idx) =>
 setForm((prev) => ({
 ...prev,
 attendees: prev.attendees.filter((_, i) => i !== idx),
 }));

 const updateAttendee = (idx, field, value) => {
 setForm((prev) => {
 const arr = [...prev.attendees];
 arr[idx] = { ...arr[idx], [field]: value };
 return { ...prev, attendees: arr };
 });
 };

 /* Photo handling */
 const handlePhotoChange = (e) => setNewPhotos(Array.from(e.target.files));

 const removeExistingPhoto = (photoId) =>
 setExistingPhotos((prev) => prev.filter((p) => p._id !== photoId));

 /* Risk Rating Preview */
 const computeRiskRating = () => {
 const l = parseInt(form.likelihoodScore);
 const c = parseInt(form.consequenceScore);
 if (!l || !c) return null;
 const score = l * c;
 if (score <= 4) return { label: "Low", color: "success" };
 if (score <= 9) return { label: "Medium", color: "warning" };
 if (score <= 16) return { label: "High", color: "danger" };
 return { label: "Extreme", color: "dark" };
 };
 const riskRating = computeRiskRating();

 /* Submit */
 const handleSubmit = async (e) => {
 e.preventDefault();
 if (!form.location.trim()) {
 toast.error("Location is required");
 return;
 }
 if (!form.description.trim()) {
 toast.error("Description is required");
 return;
 }
 if (!form.project) {
 toast.error("Project is required");
 return;
 }

 const formData = new FormData();
 const fields = {
 ...form,
 witnesses: undefined,
 correctiveActions: undefined,
 checklistItems: undefined,
 attendees: undefined,
 };

 Object.entries(fields).forEach(([k, v]) => {
 if (v !== "" && v !== null && v !== undefined) {
 formData.append(k, v);
 }
 });

 formData.append("witnesses", JSON.stringify(form.witnesses));
 formData.append("correctiveActions", JSON.stringify(form.correctiveActions));
 formData.append("checklistItems", JSON.stringify(form.checklistItems));
 formData.append("attendees", JSON.stringify(form.attendees));

 newPhotos.forEach((file) => formData.append("photos", file));

 if (isEdit) {
 formData.append(
 "existingPhotoIds",
 JSON.stringify(existingPhotos.map((p) => p._id))
 );
 }

 setLoading(true);
 try {
 if (isEdit) {
 await updateSafetyIncident(id, formData);
 toast.success("Safety record updated");
 } else {
 await createSafetyIncident(formData);
 toast.success("Safety incident reported");
 }
 navigate("/safety");
 } catch (err) {
 toast.error(err?.response?.data?.message || "Failed to save");
 } finally {
 setLoading(false);
 }
 };

 if (fetching) {
 return (
 <div className="text-center py-5">
 <Spinner animation="border" variant="primary" />
 </div>
 );
 }

 const isToolboxTalk = ["Toolbox Talk", "Safety Audit", "Compliance Check"].includes(
    form.incidentType
  );

  const filteredWorkOrders = form.project
    ? workOrders.filter(
        (wo) =>
          String(wo.project?._id || wo.project) === String(form.project) ||
          (form.workOrder && String(wo._id) === String(form.workOrder))
      )
    : workOrders;

  return (
 <>
 <PageTitle
 activeMenu={isEdit ? "Edit Safety Record" : "Report Safety Incident"}
 motherMenu="Safety"
 />

 <Form onSubmit={handleSubmit}>
 {/* Section 1: Incident Core Details */}
 <Card className="shadow-sm mb-3">
 <Card.Header>
 <h6 className="mb-0">
 <i className="fas fa-hard-hat me-2 text-warning" />
 Incident Details
 </h6>
 </Card.Header>
 <Card.Body>
 <Row className="mb-3">
 <Col md={3}>
 <Form.Group>
 <Form.Label>
 Incident Date <span className="text-danger">*</span>
 </Form.Label>
 <Form.Control
 type="date"
 name="incidentDate"
 value={form.incidentDate}
 onChange={handleChange}
 required
 />
 </Form.Group>
 </Col>
 <Col md={2}>
 <Form.Group>
 <Form.Label>Time</Form.Label>
 <Form.Control
 type="time"
 name="incidentTime"
 value={form.incidentTime}
 onChange={handleChange}
 />
 </Form.Group>
 </Col>
 <Col md={3}>
 <Form.Group>
 <Form.Label>Incident Type</Form.Label>
 <Form.Select
 name="incidentType"
 value={form.incidentType}
 onChange={handleChange}
 >
 {INCIDENT_TYPES.map((t) => (
 <option key={t} value={t}>{t}</option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={2}>
 <Form.Group>
 <Form.Label>Severity</Form.Label>
 <Form.Select name="severity" value={form.severity} onChange={handleChange}>
 {["Low", "Medium", "High", "Critical"].map((s) => (
 <option key={s} value={s}>{s}</option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={2}>
 <Form.Group>
 <Form.Label>Status</Form.Label>
 <Form.Select name="status" value={form.status} onChange={handleChange}>
 {["Draft", "Submitted", "Under Investigation", "Resolved", "Closed"].map(
 (s) => (
 <option key={s} value={s}>{s}</option>
 )
 )}
 </Form.Select>
 </Form.Group>
 </Col>
 </Row>

 <Row className="mb-3">
 <Col md={4}>
 <Form.Group>
 <Form.Label>
 Location <span className="text-danger">*</span>
 </Form.Label>
 <Form.Control
 name="location"
 value={form.location}
 onChange={handleChange}
 placeholder="e.g. Block A, 3rd Floor Slab"
 required
 />
 </Form.Group>
 </Col>
 <Col md={4}>
 <Form.Group>
 <Form.Label>
 Project <span className="text-danger">*</span>
 </Form.Label>
 <Form.Select
 name="project"
 value={form.project}
 onChange={handleChange}
 required
 >
 <option value="">— Select Project —</option>
 {projects.map((p) => (
 <option key={p._id} value={p._id}>
 {p.projectName || p.name}
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={4}>
 <Form.Group>
 <Form.Label>Work Order</Form.Label>
 <Form.Select name="workOrder" value={form.workOrder} onChange={handleChange}>
 <option value="">— Select Work Order —</option>
 {filteredWorkOrders.map((wo) => (
                      <option key={wo._id} value={wo._id}>
 {wo.woNo}{wo.label ? ` – ${wo.label}` : ""}
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 </Row>

 <Row className="mb-3">
 <Col md={12}>
 <Form.Group>
 <Form.Label>
 Description <span className="text-danger">*</span>
 </Form.Label>
 <Form.Control
 as="textarea"
 rows={3}
 name="description"
 value={form.description}
 onChange={handleChange}
 placeholder="Describe what happened, sequence of events..."
 required
 />
 </Form.Group>
 </Col>
 </Row>

 <Row className="mb-3">
 <Col md={4}>
 <Form.Group>
 <Form.Label>Reported By</Form.Label>
 <Form.Select name="reportedBy" value={form.reportedBy} onChange={handleChange}>
 <option value="">— Select User —</option>
 {users.map((u) => (
 <option key={u._id} value={u._id}>{u.name}</option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={4}>
 <Form.Group>
 <Form.Label>Investigated By</Form.Label>
 <Form.Select
 name="investigatedBy"
 value={form.investigatedBy}
 onChange={handleChange}
 >
 <option value="">— Select User —</option>
 {users.map((u) => (
 <option key={u._id} value={u._id}>{u.name}</option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={4} className="d-flex align-items-end gap-3 pb-2">
 <Form.Check
 type="switch"
 id="investigationRequired"
 name="investigationRequired"
 label="Investigation Required"
 checked={form.investigationRequired}
 onChange={handleChange}
 />
 </Col>
 </Row>
 </Card.Body>
 </Card>

 {/* Section 2: Affected Person */}
 {!isToolboxTalk && (
 <Card className="shadow-sm mb-3">
 <Card.Header>
 <h6 className="mb-0">
 <i className="fas fa-user-injured me-2 text-danger" />
 Affected Person Details
 </h6>
 </Card.Header>
 <Card.Body>
 <Row className="mb-3">
 <Col md={4}>
 <Form.Group>
 <Form.Label>Involved Person Name</Form.Label>
 <Form.Control
 name="involvedPerson"
 value={form.involvedPerson}
 onChange={handleChange}
 placeholder="Full name"
 />
 </Form.Group>
 </Col>
 <Col md={3}>
 <Form.Group>
 <Form.Label>Designation</Form.Label>
 <Form.Control
 name="designation"
 value={form.designation}
 onChange={handleChange}
 placeholder="e.g. Mason, Carpenter"
 />
 </Form.Group>
 </Col>
 <Col md={3}>
 <Form.Group>
 <Form.Label>Employer / Sub-contractor</Form.Label>
 <Form.Control
 name="employer"
 value={form.employer}
 onChange={handleChange}
 placeholder="Employer name"
 />
 </Form.Group>
 </Col>
 <Col md={2} className="d-flex align-items-end pb-2">
 <Form.Check
 type="switch"
 id="medicalTreatmentProvided"
 name="medicalTreatmentProvided"
 label="Medical Treatment"
 checked={form.medicalTreatmentProvided}
 onChange={handleChange}
 />
 </Col>
 </Row>
 <Row className="mb-3">
 <Col md={3}>
 <Form.Group>
 <Form.Label>Injury Type</Form.Label>
 <Form.Control
 name="injuryType"
 value={form.injuryType}
 onChange={handleChange}
 placeholder="e.g. Laceration, Sprain"
 />
 </Form.Group>
 </Col>
 <Col md={3}>
 <Form.Group>
 <Form.Label>Body Part Affected</Form.Label>
 <Form.Control
 name="bodyPartAffected"
 value={form.bodyPartAffected}
 onChange={handleChange}
 placeholder="e.g. Left hand, Foot"
 />
 </Form.Group>
 </Col>
 {form.medicalTreatmentProvided && (
 <Col md={6}>
 <Form.Group>
 <Form.Label>Medical Treatment Details</Form.Label>
 <Form.Control
 as="textarea"
 rows={2}
 name="medicalDetails"
 value={form.medicalDetails}
 onChange={handleChange}
 placeholder="First aid, hospital, treatment..."
 />
 </Form.Group>
 </Col>
 )}
 </Row>
 </Card.Body>
 </Card>
 )}

 {/* Section 3: Cause Analysis */}
 {!isToolboxTalk && (
 <Card className="shadow-sm mb-3">
 <Card.Header>
 <h6 className="mb-0">
 <i className="fas fa-search me-2 text-info" />
 Cause Analysis
 </h6>
 </Card.Header>
 <Card.Body>
 <Row className="mb-3">
 <Col md={6}>
 <Form.Group>
 <Form.Label>Immediate Cause</Form.Label>
 <Form.Control
 as="textarea"
 rows={2}
 name="immediateCause"
 value={form.immediateCause}
 onChange={handleChange}
 placeholder="Direct cause of the incident..."
 />
 </Form.Group>
 </Col>
 <Col md={6}>
 <Form.Group>
 <Form.Label>Root Cause</Form.Label>
 <Form.Control
 as="textarea"
 rows={2}
 name="rootCause"
 value={form.rootCause}
 onChange={handleChange}
 placeholder="Underlying systemic cause..."
 />
 </Form.Group>
 </Col>
 </Row>
 <Row>
 <Col md={6}>
 <Form.Group>
 <Form.Label>Contributing Factors</Form.Label>
 <Form.Control
 as="textarea"
 rows={2}
 name="contributingFactors"
 value={form.contributingFactors}
 onChange={handleChange}
 placeholder="Environmental, behavioral, organizational factors..."
 />
 </Form.Group>
 </Col>
 <Col md={6}>
 <Form.Group>
 <Form.Label>Preventive Measures</Form.Label>
 <Form.Control
 as="textarea"
 rows={2}
 name="preventiveMeasures"
 value={form.preventiveMeasures}
 onChange={handleChange}
 placeholder="Steps to prevent recurrence..."
 />
 </Form.Group>
 </Col>
 </Row>
 </Card.Body>
 </Card>
 )}

 {/* Section 4: Risk Assessment */}
 <Card className="shadow-sm mb-3">
 <Card.Header>
 <h6 className="mb-0">
 <i className="fas fa-exclamation-triangle me-2 text-warning" />
 Risk Assessment Matrix
 </h6>
 </Card.Header>
 <Card.Body>
 <Row className="align-items-end">
 <Col md={3}>
 <Form.Group>
 <Form.Label>Likelihood (1–5)</Form.Label>
 <Form.Select
 name="likelihoodScore"
 value={form.likelihoodScore}
 onChange={handleChange}
 >
 <option value="">— Select —</option>
 <option value={1}>1 – Rare</option>
 <option value={2}>2 – Unlikely</option>
 <option value={3}>3 – Possible</option>
 <option value={4}>4 – Likely</option>
 <option value={5}>5 – Almost Certain</option>
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={3}>
 <Form.Group>
 <Form.Label>Consequence (1–5)</Form.Label>
 <Form.Select
 name="consequenceScore"
 value={form.consequenceScore}
 onChange={handleChange}
 >
 <option value="">— Select —</option>
 <option value={1}>1 – Negligible</option>
 <option value={2}>2 – Minor</option>
 <option value={3}>3 – Moderate</option>
 <option value={4}>4 – Major</option>
 <option value={5}>5 – Catastrophic</option>
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={3} className="pb-1">
 {riskRating && (
 <div>
 <small className="text-muted d-block mb-1">Risk Rating</small>
 <Badge bg={riskRating.color} style={{ fontSize: "1rem", padding: "0.4rem 0.8rem" }}>
 {riskRating.label}
 {" "}({parseInt(form.likelihoodScore) * parseInt(form.consequenceScore)})
 </Badge>
 </div>
 )}
 </Col>
 </Row>
 </Card.Body>
 </Card>

 {/* Section 5: Corrective Actions */}
 <Card className="shadow-sm mb-3">
 <Card.Header className="d-flex justify-content-between align-items-center">
 <h6 className="mb-0">
 <i className="fas fa-tasks me-2 text-primary" />
 Corrective Actions
 <Badge bg="secondary" className="ms-2">{form.correctiveActions.length}</Badge>
 </h6>
 <Button size="sm" variant="outline-primary" type="button" onClick={addCorrectiveAction}>
 <i className="fas fa-plus me-1" /> Add Action
 </Button>
 </Card.Header>
 <Card.Body className="p-0">
 {form.correctiveActions.length === 0 ? (
 <div className="text-center py-4 text-muted">
 <i className="fas fa-tasks fa-2x mb-2 d-block" />
 No corrective actions added yet.
 </div>
 ) : (
 <div className="table-responsive">
 <Table size="sm" className="mb-0 align-middle">
 <thead className="table-light">
 <tr>
 <th style={{ width: 260 }}>Action</th>
 <th style={{ width: 160 }}>Assigned To</th>
 <th style={{ width: 130 }}>Due Date</th>
 <th style={{ width: 120 }}>Status</th>
 <th>Remarks</th>
 <th style={{ width: 40 }}></th>
 </tr>
 </thead>
 <tbody>
 {form.correctiveActions.map((ca, idx) => (
 <tr key={idx}>
 <td>
 <Form.Control
 size="sm"
 value={ca.action}
 onChange={(e) => updateCA(idx, "action", e.target.value)}
 placeholder="Describe corrective action"
 />
 </td>
 <td>
 <Form.Select
 size="sm"
 value={ca.assignedTo}
 onChange={(e) => updateCA(idx, "assignedTo", e.target.value)}
 >
 <option value="">— User —</option>
 {users.map((u) => (
 <option key={u._id} value={u._id}>{u.name}</option>
 ))}
 </Form.Select>
 </td>
 <td>
 <Form.Control
 size="sm"
 type="date"
 value={ca.dueDate}
 onChange={(e) => updateCA(idx, "dueDate", e.target.value)}
 />
 </td>
 <td>
 <Form.Select
 size="sm"
 value={ca.status}
 onChange={(e) => updateCA(idx, "status", e.target.value)}
 >
 {["Open", "In Progress", "Completed", "Overdue"].map((s) => (
 <option key={s} value={s}>{s}</option>
 ))}
 </Form.Select>
 </td>
 <td>
 <Form.Control
 size="sm"
 value={ca.remarks}
 onChange={(e) => updateCA(idx, "remarks", e.target.value)}
 placeholder="Optional"
 />
 </td>
 <td>
 <Button
 size="sm"
 variant="outline-danger"
 type="button"
 onClick={() => removeCorrectiveAction(idx)}
 >
 <i className="fas fa-trash" />
 </Button>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 </div>
 )}
 </Card.Body>
 </Card>

 {/* Section 6: Compliance Checklist */}
 <Card className="shadow-sm mb-3">
 <Card.Header className="d-flex justify-content-between align-items-center">
 <h6 className="mb-0">
 <i className="fas fa-clipboard-check me-2 text-success" />
 Compliance Checklist
 <Badge bg="secondary" className="ms-2">{form.checklistItems.length}</Badge>
 </h6>
 <Button size="sm" variant="outline-success" type="button" onClick={addChecklistItem}>
 <i className="fas fa-plus me-1" /> Add Item
 </Button>
 </Card.Header>
 <Card.Body className="p-0">
 {form.checklistItems.length === 0 ? (
 <div className="text-center py-4 text-muted">
 <i className="fas fa-clipboard fa-2x mb-2 d-block" />
 No checklist items. Add manually or select Safety Audit / Compliance Check type.
 </div>
 ) : (
 <div className="table-responsive">
 <Table size="sm" className="mb-0 align-middle">
 <thead className="table-light">
 <tr>
 <th>Checklist Item</th>
 <th style={{ width: 120 }}>Compliant?</th>
 <th style={{ width: 200 }}>Remarks</th>
 <th style={{ width: 40 }}></th>
 </tr>
 </thead>
 <tbody>
 {form.checklistItems.map((item, idx) => (
 <tr key={idx}>
 <td>
 <Form.Control
 size="sm"
 value={item.item}
 onChange={(e) => updateChecklist(idx, "item", e.target.value)}
 placeholder="Checklist item description"
 />
 </td>
 <td>
 <Form.Select
 size="sm"
 value={item.compliant}
 onChange={(e) => updateChecklist(idx, "compliant", e.target.value)}
 >
 {["Pending", "Yes", "No", "NA"].map((c) => (
 <option key={c} value={c}>{c}</option>
 ))}
 </Form.Select>
 </td>
 <td>
 <Form.Control
 size="sm"
 value={item.remarks}
 onChange={(e) => updateChecklist(idx, "remarks", e.target.value)}
 placeholder="Optional"
 />
 </td>
 <td>
 <Button
 size="sm"
 variant="outline-danger"
 type="button"
 onClick={() => removeChecklistItem(idx)}
 >
 <i className="fas fa-trash" />
 </Button>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 </div>
 )}
 </Card.Body>
 </Card>

 {/* Section 7: Witnesses */}
 {!isToolboxTalk && (
 <Card className="shadow-sm mb-3">
 <Card.Header className="d-flex justify-content-between align-items-center">
 <h6 className="mb-0">
 <i className="fas fa-users me-2 text-secondary" />
 Witnesses
 <Badge bg="secondary" className="ms-2">{form.witnesses.length}</Badge>
 </h6>
 <Button size="sm" variant="outline-secondary" type="button" onClick={addWitness}>
 <i className="fas fa-plus me-1" /> Add Witness
 </Button>
 </Card.Header>
 <Card.Body className="p-0">
 {form.witnesses.length === 0 ? (
 <div className="text-center py-4 text-muted">No witnesses added.</div>
 ) : (
 <div className="table-responsive">
 <Table size="sm" className="mb-0 align-middle">
 <thead className="table-light">
 <tr>
 <th style={{ width: 200 }}>Name</th>
 <th style={{ width: 150 }}>Designation</th>
 <th>Statement</th>
 <th style={{ width: 40 }}></th>
 </tr>
 </thead>
 <tbody>
 {form.witnesses.map((w, idx) => (
 <tr key={idx}>
 <td>
 <Form.Control
 size="sm"
 value={w.name}
 onChange={(e) => updateWitness(idx, "name", e.target.value)}
 placeholder="Full name"
 />
 </td>
 <td>
 <Form.Control
 size="sm"
 value={w.designation}
 onChange={(e) =>
 updateWitness(idx, "designation", e.target.value)
 }
 placeholder="Designation"
 />
 </td>
 <td>
 <Form.Control
 size="sm"
 value={w.statement}
 onChange={(e) =>
 updateWitness(idx, "statement", e.target.value)
 }
 placeholder="Witness statement"
 />
 </td>
 <td>
 <Button
 size="sm"
 variant="outline-danger"
 type="button"
 onClick={() => removeWitness(idx)}
 >
 <i className="fas fa-trash" />
 </Button>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 </div>
 )}
 </Card.Body>
 </Card>
 )}

 {/* Section 8: Toolbox Talk Attendees */}
 {form.incidentType === "Toolbox Talk" && (
 <Card className="shadow-sm mb-3">
 <Card.Header className="d-flex justify-content-between align-items-center">
 <h6 className="mb-0">
 <i className="fas fa-chalkboard-teacher me-2 text-primary" />
 Toolbox Talk Details
 </h6>
 <Button size="sm" variant="outline-primary" type="button" onClick={addAttendee}>
 <i className="fas fa-plus me-1" /> Add Attendee
 </Button>
 </Card.Header>
 <Card.Body>
 <Row className="mb-3">
 <Col md={6}>
 <Form.Group>
 <Form.Label>Talk Topic</Form.Label>
 <Form.Control
 name="toolboxTalkTopic"
 value={form.toolboxTalkTopic}
 onChange={handleChange}
 placeholder="e.g. Working at Height Safety, PPE Usage"
 />
 </Form.Group>
 </Col>
 <Col md={2}>
 <Form.Group>
 <Form.Label>Total Attendees</Form.Label>
 <Form.Control
 type="number"
 name="attendeesCount"
 value={form.attendeesCount}
 onChange={handleChange}
 min={0}
 />
 </Form.Group>
 </Col>
 </Row>
 {form.attendees.length > 0 && (
 <div className="table-responsive">
 <Table size="sm" className="mb-0 align-middle">
 <thead className="table-light">
 <tr>
 <th style={{ width: 200 }}>Name</th>
 <th style={{ width: 180 }}>Designation</th>
 <th style={{ width: 40 }}></th>
 </tr>
 </thead>
 <tbody>
 {form.attendees.map((a, idx) => (
 <tr key={idx}>
 <td>
 <Form.Control
 size="sm"
 value={a.name}
 onChange={(e) => updateAttendee(idx, "name", e.target.value)}
 placeholder="Attendee name"
 />
 </td>
 <td>
 <Form.Control
 size="sm"
 value={a.designation}
 onChange={(e) =>
 updateAttendee(idx, "designation", e.target.value)
 }
 placeholder="Designation"
 />
 </td>
 <td>
 <Button
 size="sm"
 variant="outline-danger"
 type="button"
 onClick={() => removeAttendee(idx)}
 >
 <i className="fas fa-trash" />
 </Button>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 </div>
 )}
 </Card.Body>
 </Card>
 )}

 {/* Section 9: Lessons Learned & Photos */}
 <Card className="shadow-sm mb-3">
 <Card.Header>
 <h6 className="mb-0">
 <i className="fas fa-lightbulb me-2 text-warning" />
 Lessons Learned & Evidence
 </h6>
 </Card.Header>
 <Card.Body>
 <Row className="mb-3">
 <Col md={6}>
 <Form.Group>
 <Form.Label>Lessons Learned</Form.Label>
 <Form.Control
 as="textarea"
 rows={3}
 name="lessonsLearned"
 value={form.lessonsLearned}
 onChange={handleChange}
 placeholder="Key takeaways for safety improvement..."
 />
 </Form.Group>
 </Col>
 <Col md={6}>
 <Form.Group>
 <Form.Label>
 <i className="fas fa-camera me-1" /> Site Photos / Evidence
 </Form.Label>
 <Form.Control
 type="file"
 accept="image/*"
 multiple
 onChange={handlePhotoChange}
 />
 <Form.Text className="text-muted">
 Attach photos as evidence. JPG, PNG supported.
 </Form.Text>
 </Form.Group>
 </Col>
 </Row>

 {isEdit && existingPhotos.length > 0 && (
 <div>
 <p className="fw-semibold mb-2">Existing Photos:</p>
 <div className="d-flex flex-wrap gap-2">
 {existingPhotos.map((photo) => (
 <div
 key={photo._id}
 className="position-relative border rounded p-1"
 style={{ width: 90 }}
 >
 <img
 src={`${process.env.REACT_APP_API_URL}${photo.fileUrl}`}
 alt={photo.fileName}
 className="img-fluid rounded"
 style={{ height: 70, objectFit: "cover", width: "100%" }}
 onError={(e) => {
 e.currentTarget.onerror = null;
 e.currentTarget.src = "/logo192.png";
 }}
 />
 <Button
 size="sm"
 variant="danger"
 className="position-absolute top-0 end-0 p-0"
 style={{ width: 20, height: 20, fontSize: 10, lineHeight: 1 }}
 type="button"
 onClick={() => removeExistingPhoto(photo._id)}
 >
 ×
 </Button>
 </div>
 ))}
 </div>
 </div>
 )}
 </Card.Body>
 </Card>

 {/* Submit */}
 <div className="d-flex gap-2 mb-4">
 <Button type="submit" variant="primary" disabled={loading}>
 {loading ? (
 <>
 <Spinner size="sm" className="me-2" />
 {isEdit ? "Updating..." : "Saving..."}
 </>
 ) : isEdit ? (
 "Update Record"
 ) : (
 "Submit Incident Report"
 )}
 </Button>
 <Button
 variant="outline-secondary"
 type="button"
 onClick={() => navigate("/safety")}
 >
 Cancel
 </Button>
 </div>
 </Form>
 </>
 );
};

export default SafetyForm;
